import React from 'react';
import Link from 'components/common/Link';
import { Box, Typography } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import type { BreadCrumb } from 'components/AuthLayout';

export const getSettingsBrowserTitle = (breadCrumb?: BreadCrumb): string | null => {
  if (!breadCrumb) {
    return null;
  }

  const parts: string[] = ['Settings'];

  const parentTitle = breadCrumb.parent?.titleText ?? breadCrumb.parent?.title;
  if (typeof parentTitle === 'string' && parentTitle !== 'Settings') {
    parts.push(parentTitle);
  }

  const subParentTitle = breadCrumb.subParent?.titleText ?? breadCrumb.subParent?.title;
  if (typeof subParentTitle === 'string') {
    parts.push(subParentTitle);
  }

  if (breadCrumb.titleText) {
    if (parts[parts.length - 1] !== breadCrumb.titleText) {
      parts.push(breadCrumb.titleText);
    }
  }

  if (parts.length <= 1) {
    return null;
  }

  return parts.join(' > ');
};

export const settingsBreadCrumb = (title: string, parentTitle?: string) => {
  return {
    parent: { title: 'Settings', link: '/settings', titleText: 'Settings' },
    subParent: parentTitle
      ? {
          title: parentTitle,
          link: `/settings#${parentTitle.toLowerCase().replace(' ', '-')}`,
          titleText: parentTitle,
        }
      : undefined,
    title,
    titleText: title,
  };
};

export const renderSection = (
  items: { title: string; description: string; link?: string; content?: React.ReactNode }[],
) => {
  return (
    <Box mb={4} mt={4} sx={{ cursor: 'pointer' }}>
      {items.map(item => {
        const content = (
          <Box
            key={item.title}
            p={2}
            bgcolor="background.paper"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <div>
              <Typography variant="h6">{item.title}</Typography>
              <Typography variant="body2" color="textSecondary">
                {item.description}
              </Typography>

              {item.content ? <Box mt={2}>{item.content}</Box> : null}
            </div>

            {item.link && <ArrowForwardIosIcon fontSize="small" style={{ color: '#9e9e9e' }} />}
          </Box>
        );

        return item.link ? (
          <Link href={item.link} style={{ textDecoration: 'none', color: 'inherit' }}>
            {content}
          </Link>
        ) : (
          content
        );
      })}
    </Box>
  );
};
